import { useCallback, useRef, useState } from "react";
import { imageFileToDataUri } from "../imageUtils";

/** At most this many screenshots per report — mirrors the API's budget. */
export const MAX_SCREENSHOTS = 3;

/** An attached screenshot with a render key that survives duplicates. */
export interface Screenshot {
  /** Monotonic id — two identical pastes must not share a React key. */
  id: number;
  /** The downscaled JPEG data URI submitted with the report. */
  dataUri: string;
}

/** Attached-screenshot state plus the operations the modal drives it with. */
export interface FeedbackScreenshots {
  /** Currently attached screenshots, in attach order. */
  screenshots: Screenshot[];
  /** Validation message from the most recent attach attempt, if any. */
  error: string | null;
  /** Downscale and attach every image among `files`, capped and validated. */
  addImages: (files: Iterable<File | Blob>) => Promise<void>;
  /** Detach the screenshot with this id. */
  removeScreenshot: (id: number) => void;
  /** Drop every attachment and any error — used when the modal re-opens. */
  reset: () => void;
}

/**
 * Screenshot attachment for a feedback report: validation, the cap, the
 * canvas downscale, and the error message a rejected image produces. Split
 * out of the modal so the modal owns only the form
 * (docs/code-guidelines/components.md — "Extract when a component has
 * multiple responsibilities").
 */
export function useFeedbackScreenshots(): FeedbackScreenshots {
  const [screenshots, setScreenshots] = useState<Screenshot[]>([]);
  const [error, setError] = useState<string | null>(null);
  const nextScreenshotIdRef = useRef(0);

  // Deliberately NOT memoized: it reads `screenshots` for the room precheck,
  // so a stable identity would close over a stale count. Nothing downstream
  // memoizes on its identity.
  async function addImages(files: Iterable<File | Blob>) {
    setError(null);
    const incoming = Array.from(files);
    // Both validations run BEFORE the canvas downscale — attaching ten
    // files must not re-encode ten images just to throw seven away — and
    // both errors are raised here, outside any state updater (updaters must
    // stay pure; StrictMode double-invokes them).
    const images = incoming.filter((file) => file.type.startsWith("image/"));
    if (images.length < incoming.length) {
      setError("Only images can be attached.");
    }
    const room = MAX_SCREENSHOTS - screenshots.length;
    if (images.length > room) {
      setError(`You can attach up to ${MAX_SCREENSHOTS} images.`);
    }
    const accepted = images.slice(0, Math.max(0, room));
    for (const file of accepted) {
      let dataUri: string;
      try {
        dataUri = await imageFileToDataUri(file);
      } catch (addError) {
        setError(
          addError instanceof Error
            ? addError.message
            : "Could not add the image."
        );
        continue;
      }
      const screenshot: Screenshot = {
        id: nextScreenshotIdRef.current++,
        dataUri
      };
      setScreenshots((previous) =>
        // Belt over the braces above: concurrent addImages calls can race
        // past the `room` precheck — the cap itself must still hold.
        previous.length >= MAX_SCREENSHOTS
          ? previous
          : [...previous, screenshot]
      );
    }
  }

  const removeScreenshot = useCallback((id: number) => {
    setScreenshots((previous) => previous.filter((item) => item.id !== id));
  }, []);

  const reset = useCallback(() => {
    setScreenshots([]);
    setError(null);
  }, []);

  return { screenshots, error, addImages, removeScreenshot, reset };
}
